/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ColorProps } from '../utils/UNSAFE_interpolations/colors';
import { LineAreaItem } from '../UNSAFE_LineAreaChart/lineAreaChart.types';
import { Marker, MarkerShapes, MARKER_PADDING } from '../UNSAFE_SvgShapes';
import { getGroupCenterCoord } from '../utils/PRIVATE_chartUtils/layoutUtils';
import { Group } from '../UNSAFE_Axis/axis.types';
import { ChartSeries, Scale } from '.';
import { getMarkerFillAndStroke } from '../utils/PRIVATE_chartUtils/utils';
import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';
import type { BundleType } from '../resources/nls/bundle';
import { getItemAriaLabel } from '../utils/PRIVATE_chartUtils/accUtils';
import { supportsMobileScreenReader } from '../utils/UNSAFE_visUtils';

const MARKER_DEFAULT_SIZE = 10;
type MarkersProps<K, D> = {
  yScale: Scale;
  xScale: Scale;
  startIndex: number;
  endIndex: number;
  seriesIndex: number;
  series: ChartSeries<K, D>;
  markerType: MarkerShapes;
  data: number[];
  color: ColorProps['color'];
  groups: Group[];
  focusedItemIndex?: number;
  isSelectionEnabled: boolean;
  hoveredItemIndex?: number;
  selectedIds?: Set<string | number>;
  hiddenIds?: Set<string | number>;
  activeId?: string;
  orientation: 'horizontal' | 'vertical';
  isDrillEnabled: boolean;
  isLog: boolean;
  getDataItem: (seriesIndex: number, groupIndex: number) => D | undefined;
  timeAxisType?: 'enabled' | 'skipGaps' | 'mixedFrequency';
};

export function Markers<K extends string | number, D extends LineAreaItem<K>>({
  yScale,
  xScale,
  color,
  seriesIndex,
  startIndex,
  endIndex,
  hiddenIds,
  selectedIds,
  orientation,
  isLog,
  data,
  markerType,
  getDataItem,
  focusedItemIndex,
  hoveredItemIndex,
  isDrillEnabled,
  activeId,
  isSelectionEnabled,
  timeAxisType,
  series,
  groups
}: MarkersProps<K, D>) {
  const isHoriz = orientation === 'horizontal';
  const items = [];

  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');

  for (let groupIndex = startIndex; groupIndex < endIndex + 1; groupIndex++) {
    const item = getDataItem(seriesIndex, groupIndex);
    const value = data[groupIndex];
    if (!item || hiddenIds?.has(item.id) || (isLog && value <= 0)) {
      continue;
    }
    const x = isHoriz
      ? yScale.transform(value)
      : getGroupCenterCoord(groups[groupIndex], groupIndex, xScale, item, timeAxisType);
    const y = isHoriz
      ? getGroupCenterCoord(groups[groupIndex], groupIndex, xScale, item, timeAxisType)
      : yScale.transform(value);
    const isFocused = focusedItemIndex === groupIndex;
    const isHovered = hoveredItemIndex === groupIndex;
    const isSelected = !!selectedIds?.has(item.id!);
    const isDrillable = item.drilling === 'on' || (item.drilling != 'off' && isDrillEnabled);
    const { fill, stroke, outerStroke } = getMarkerFillAndStroke(
      !!item.isMarkerDisplayed,
      isSelectionEnabled,
      isHovered,
      isFocused,
      isSelected,
      color,
      item.markerColor
    );
    const itemMarkerType = item.isMarkerDisplayed ? item.markerType || markerType : markerType;
    const isActive = isFocused || isHovered;
    const ariaLabel =
      supportsMobileScreenReader || isActive
        ? getItemAriaLabel(
            translations,
            series.name || series.id.toString(),
            groups[groupIndex].name || groups[groupIndex].id,
            item.value,
            isSelectionEnabled,
            isSelected,
            isDrillable,
            item.accessibleLabel
          )
        : undefined;

    const dataInfo = {
      'data-oj-series-index': seriesIndex,
      'data-oj-group-index': groupIndex,
      'data-oj-object': 'item',
      id: isActive ? activeId : undefined,
      role: ariaLabel ? 'img' : undefined,
      'aria-label': ariaLabel
    };

    const markerSize = (item.markerSize || MARKER_DEFAULT_SIZE) + 2 * MARKER_PADDING;
    items.push(
      <Marker
        type={itemMarkerType}
        tx={x}
        ty={y}
        isInteractive={isDrillable || isSelectionEnabled}
        scale={1}
        fill={fill}
        stroke={stroke}
        outerStroke={outerStroke}
        width={markerSize}
        height={markerSize}
        dataInfo={dataInfo}
        preserveRectAspectRatio
      />
    );
  }
  return <>{items}</>;
}
